import type { PrintReceiptRaster, ReceiptDto, StationTicket } from '@gc/shared';

import { blocksForSegment, isDrawableHeight, rasterFromSegments } from './rasterSegments';
import {
  layoutReceipt,
  RECEIPT_DOTS,
  type Block,
  type Icon,
  type Measure,
  type ReceiptLayout,
} from './receiptLayout';
import { layoutStationTicket } from './stationLayout';

/** Matches the layout's own cell padding, so text sits where it was measured. */
const CELL_PAD = 8;

/**
 * Line weight, in dots, for every rule the table draws.
 *
 * The same number the layout uses for its horizontal rules, so a box has no
 * thin side. One dot is below what this paper reliably renders.
 */
const RULE_DOTS = 3;

/**
 * Paint the receipt the layout worked out, and pack it into printer dots.
 *
 * DELIBERATELY THIN. Every decision — column widths, wrapping, row heights,
 * where each block sits — was made by `receiptLayout.ts`, which is pure and
 * tested. This file only pushes pixels, because no DOM emulation ships in this
 * workspace and anything touching `getContext` here cannot be verified except
 * on paper.
 *
 * The venue LOGO is not drawn here. The staff app cannot reach the packed logo
 * rows — the settings endpoint answers only whether one is configured — so the
 * server prepends its own copy above this image.
 */

/** White paper, black ink: what `packToPrinterBits` expects to read back. */
const PAPER = '#ffffff';
const INK = '#000000';

function measureWith(context: CanvasRenderingContext2D) {
  return (text: string, font: string): number => {
    context.font = font;
    return context.measureText(text).width;
  };
}

function drawBlock(context: CanvasRenderingContext2D, block: Block): void {
  if (block.kind === 'gap') return;

  if (block.kind === 'rule') {
    // A real hairline, which is the whole reason for drawing rather than typing:
    // the typed receipt could only approximate this with a row of underscores.
    // Inset to its own span, so the table's rules close its box rather than
    // running off both edges of the paper.
    context.fillStyle = INK;
    context.fillRect(block.x, block.y, block.width, block.height);
    return;
  }

  if (block.kind === 'pair') {
    // Label left, money hard against the right margin — the money column is
    // what a customer scans down, so it never yields to a long label.
    context.fillStyle = INK;
    context.font = block.font;
    context.textBaseline = 'top';
    context.textAlign = 'left';
    context.fillText(block.left, 12, block.y + 4);
    context.textAlign = 'right';
    context.fillText(block.right, RECEIPT_DOTS - 12, block.y + 4);
    return;
  }

  if (block.kind === 'iconline') {
    const size = Math.round(block.height * 0.66);
    const top = block.y + Math.round((block.height - size) / 2);
    context.fillStyle = INK;
    context.font = block.font;
    context.textBaseline = 'middle';

    drawIcon(context, block.left.icon, 12, top, size);
    context.textAlign = 'left';
    context.fillText(block.left.text, 12 + size + 8, block.y + block.height / 2);

    if (block.right) {
      // Measured so the icon sits immediately before its number rather than at
      // a guessed offset — a phone number's width changes with the venue.
      const width = context.measureText(block.right.text).width;
      const iconX = RECEIPT_DOTS - 12 - width - 8 - size;
      drawIcon(context, block.right.icon, iconX, top, size);
      context.textAlign = 'right';
      context.fillText(block.right.text, RECEIPT_DOTS - 12, block.y + block.height / 2);
    }
    return;
  }

  if (block.kind === 'text') {
    context.fillStyle = INK;
    context.font = block.font;
    context.textBaseline = 'top';
    context.textAlign = block.align === 'centre' ? 'center' : block.align;
    const x =
      block.align === 'centre'
        ? RECEIPT_DOTS / 2
        : block.align === 'right'
          ? RECEIPT_DOTS - 12
          : 12;
    context.fillText(block.text, x, block.y + 4);
    return;
  }

  // A boxed row: both outer edges and every internal division, like the
  // reference receipt — without the outer pair a table reads as loose columns.
  // The footer strip asks for `boxed: false`: it wants the dividers between
  // cashier, date and time, but no frame around them.
  context.fillStyle = INK;
  context.textBaseline = 'top';
  const tallest = Math.max(...block.cells.map((cell) => cell.lines.length));
  const lineHeight = Math.floor((block.height - CELL_PAD * 2) / tallest);

  /*
   * VERTICALS AT THE SAME WEIGHT AS THE HORIZONTALS.
   *
   * They were one dot while every horizontal rule was two or three, and on
   * 203 dpi paper a single dot column prints faint or drops out altogether —
   * the table read as rows with no columns. Every line in the owner's mockup is
   * the same weight, so they are here too.
   *
   * Each row draws its own verticals, and rows abut, so the columns come out as
   * unbroken lines down the whole table.
   */
  if (block.boxed) {
    context.fillRect(block.x, block.y, RULE_DOTS, block.height);
    context.fillRect(block.x + block.width - RULE_DOTS, block.y, RULE_DOTS, block.height);
  }

  for (const [index, cell] of block.cells.entries()) {
    if (index > 0) context.fillRect(cell.x, block.y, RULE_DOTS, block.height);

    context.font = block.font;
    context.textAlign =
      cell.align === 'centre' ? 'center' : cell.align === 'right' ? 'right' : 'left';
    const x =
      cell.align === 'centre'
        ? cell.x + cell.width / 2
        : cell.align === 'right'
          ? cell.x + cell.width - CELL_PAD
          : cell.x + CELL_PAD;

    for (const [line, text] of cell.lines.entries()) {
      context.fillText(text, x, block.y + CELL_PAD + line * lineHeight);
    }
  }
}

/**
 * The two header glyphs, drawn as PATHS.
 *
 * Vector rather than a character on purpose: a thermal printer's own font has
 * no pin or handset, and an emoji would arrive as a multi-byte sequence the
 * printer draws as line noise. Drawn at the text's own size so each sits on the
 * same line as the address or number beside it.
 */
function drawIcon(
  context: CanvasRenderingContext2D,
  icon: Icon,
  x: number,
  y: number,
  size: number,
): void {
  context.save();
  context.translate(x, y);
  context.fillStyle = INK;
  context.strokeStyle = INK;

  if (icon === 'pin') {
    const radius = size * 0.34;
    context.beginPath();
    context.arc(size / 2, radius + 1, radius, Math.PI, 0);
    context.lineTo(size / 2, size);
    context.closePath();
    context.fill();
    // The hole, punched back out in paper white so a pin reads as a pin.
    context.fillStyle = PAPER;
    context.beginPath();
    context.arc(size / 2, radius + 1, radius * 0.4, 0, Math.PI * 2);
    context.fill();
  } else {
    context.lineWidth = Math.max(2, size * 0.16);
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(size * 0.24, size * 0.24);
    context.quadraticCurveTo(size * 0.5, size * 0.6, size * 0.78, size * 0.82);
    context.stroke();
    context.beginPath();
    context.arc(size * 0.24, size * 0.24, size * 0.17, 0, Math.PI * 2);
    context.fill();
    context.beginPath();
    context.arc(size * 0.78, size * 0.82, size * 0.17, 0, Math.PI * 2);
    context.fill();
  }

  context.restore();
}

/**
 * A laid-out receipt or ticket as printer dots, painted a SEGMENT at a time.
 *
 * WHY SEGMENTS (2026-09-28). This used to paint the whole receipt on one
 * canvas, and refused anything over 4,096 dots — so every long tab printed
 * typed, and the owner saw its rules come out dashed. The ceiling is 16,384
 * dots now, and one canvas that tall is what an iPad will not reliably give:
 * Safari caps canvas area and total canvas memory, and a canvas it refuses
 * paints nothing rather than throwing. So each segment of at most 4,096 rows
 * is painted with the whole layout shifted up by the segment's top, read back,
 * and packed; `rasterSegments.ts` (pure, tested) decides the cuts and joins
 * the packed rows into the one raster the server always received.
 *
 * ONE canvas element, resized per segment, rather than a fresh one each time:
 * Safari counts every canvas not yet garbage-collected against its memory cap,
 * and resizing replaces the backing store in place. Shrunk to nothing at the
 * end so the last 9 MB is handed back at once instead of whenever GC runs.
 */
function layoutToRaster(
  layout: ReceiptLayout,
  canvas: HTMLCanvasElement,
): PrintReceiptRaster | null {
  // Checked BEFORE painting: a receipt over the ceiling prints typed, and the
  // old order painted it first — a canvas taller than the iPad would allow.
  if (!isDrawableHeight(layout.heightDots)) return null;

  try {
    return rasterFromSegments(layout.heightDots, (segment) => {
      // Setting the height — even to the same value — clears the bitmap and
      // resets the context, so paper and transform are both set after it.
      canvas.height = segment.height;
      const context = canvas.getContext('2d');
      if (!context) return null;

      // An unfilled canvas is transparent, and transparent packs as paper only
      // by accident of the alpha threshold.
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.fillStyle = PAPER;
      context.fillRect(0, 0, RECEIPT_DOTS, segment.height);

      // The layout's own coordinates, shifted so this segment's first row is
      // row 0 — the canvas clips whatever falls outside it. Whole dots only,
      // so a rule or glyph cut at a segment edge rejoins without a seam.
      context.setTransform(1, 0, 0, 1, 0, -segment.top);
      for (const block of blocksForSegment(layout.blocks, segment)) drawBlock(context, block);

      return context.getImageData(0, 0, RECEIPT_DOTS, segment.height).data;
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** A canvas at the paper's width, and a measure that reads its font metrics. */
function measuringCanvas(): { canvas: HTMLCanvasElement; measure: Measure } | null {
  const canvas = document.createElement('canvas');
  canvas.width = RECEIPT_DOTS;
  canvas.height = 1;

  const scratch = canvas.getContext('2d');
  if (!scratch) return null;
  return { canvas, measure: measureWith(scratch) };
}

/**
 * The receipt as printer dots, ready to POST.
 *
 * Returns null rather than throwing when the paper would come out longer than
 * the wire accepts: a party of thirty devices is a real receipt, and the right
 * answer there is to fall back to the typed one, not to fail the checkout.
 * Since 2026-09-28 that is past 16,384 dots (~2 m), not 4,096.
 */
export function receiptToRaster(receipt: ReceiptDto): PrintReceiptRaster | null {
  const measuring = measuringCanvas();
  if (!measuring) return null;

  // Measured first: the height cannot be known until every name has been
  // wrapped, and wrapping needs the font metrics.
  const layout = layoutReceipt(receipt, measuring.measure);
  return layoutToRaster(layout, measuring.canvas);
}

/**
 * A station ticket as printer dots — the kitchen, bar and shisha chits.
 *
 * Same painter, same packing, different arrangement. Returns null on anything
 * that would not fit the wire, for the same reason the receipt does: the typed
 * ticket is a perfectly good fallback and a failed draw must never cost the
 * kitchen its order.
 */
export function stationTicketToRaster(ticket: StationTicket): PrintReceiptRaster | null {
  const measuring = measuringCanvas();
  if (!measuring) return null;

  const layout = layoutStationTicket(ticket, measuring.measure);
  return layoutToRaster(layout, measuring.canvas);
}
